// Back-office mock-up data: the library (library_asset + library_asset_file, category scopes
// GRADUATION / RESOURCE / PATTERN, modele-de-donnees.md §4.10 and §13).
//
// Graduations: the one style GobeletCup has delivered is real. Its 46 SVG sit in
// public/catalog/graduations/ (byte-identical to Documents client/Graduations), one per reference
// × zone × placement, and its coverage below is what was actually received. The other styles
// carry the placeholder names the configurator prototype uses, with no file: the client's
// placement document asks for eight themes of several styles each but names none of them.
// Resources and patterns (options RES-03 and EDI-09) are simulated the way the prototype does.
import { localized, localizedTags } from '@/mocks/adminLocales';
import { GOBELETCUP_GRAPHIC_RESOURCES } from '@/mocks/gobeletcupGraphicResources';
import { EPrintStyle } from '@/typescript/enums/EPrintStyle';
import type { IAdminLocalized, IAdminLocalizedTags, IAdminTranslations } from '@/mocks/adminLocales';

type TGraduationSlotKind = 'large' | 'screen' | 'small';

// Icons of the app's own set that stand in for pictograms, the ones the configurator's resource
// library uses (gobeletcupGraphicResources); BaseIcon resolves them by name.
type TAdminAssetIcon = string;

// A classification entry, administrable (ADM-03, ADM-05, RES-02, RES-03, EDI-09). The French
// label is what the lists key on; English and Portuguese ride along (Katia, 2026-10-06).
interface IAdminCategory {
    id: string;
    label: string;
    subCategories?: string[];
    // Keyed by the sub-category's French label.
    subCategoryTranslations?: Record<string, IAdminTranslations>;
    translations?: IAdminTranslations;
}

interface IAdminGraduationStyle {
    // DC1-04 (signed quote, 08/10/2026): the print styles it is offered in, all of them when null,
    // and whether the customer may change its colour, set the way a resource's are.
    compatiblePrintStyles: EPrintStyle[] | null;
    // Slot keys (`ECO300:screen`, `ECO400:small:top`) with a received file.
    files: string[];
    // RES-02: graduations are filed by theme and by graphic style.
    graphicStyleId: string;
    id: string;
    isActive: boolean;
    isRecolorable: boolean;
    // Shown to the customer under the design in the configurator, hence in its three languages.
    name: IAdminLocalized;
    themeId: string;
}

interface IAdminLibraryAsset {
    categoryId: string;
    compatiblePrintStyles: EPrintStyle[] | null;
    description: IAdminLocalized;
    id: string;
    isActive: boolean;
    isRecolorable: boolean;
    kind: 'pattern' | 'resource';
    name: IAdminLocalized;
    // null = every reference.
    referenceCodes: string[] | null;
    // What the mock-up draws in place of the SVG: a path in a 24 × 24 box, or an icon name.
    shape: { icon?: TAdminAssetIcon; path?: string; pattern?: string };
    // Resource: height when added to a design. Pattern: size of one repeat.
    sizeMm: number;
    svgFileName: string;
    tags: IAdminLocalizedTags;
}

const ADMIN_GRADUATION_THEMES: IAdminCategory[] = [
    { id: 'classiques', label: 'Classique', translations: { en: 'Classic', pt: 'Clássico' } },
    { id: 'meteo', label: 'Météo & température', translations: { en: 'Weather & temperature', pt: 'Tempo & temperatura' } },
    { id: 'performance', label: 'Performance & niveau', translations: { en: 'Performance & level', pt: 'Desempenho & nível' } },
    { id: 'humeur', label: 'Humeur', translations: { en: 'Mood', pt: 'Humor' } },
    { id: 'apero', label: 'Fête & apéro', translations: { en: 'Party & drinks', pt: '' } },
    { id: 'sport', label: 'Sport & défis', translations: { en: 'Sport & challenges', pt: 'Desporto & desafios' } },
    { id: 'parcours', label: 'Parcours & progression', translations: { en: 'Path & progress', pt: '' } },
    { id: 'decale', label: 'Décalé & humour', translations: { en: 'Quirky & funny', pt: '' } },
];

// What was delivered, per zone: the folders of Documents client/Graduations/Export_SVG_Graduation.
const SCREEN_REFERENCES = [
    'ECO30',
    'ECO50',
    'ECO120',
    'ECO180',
    'ECO200',
    'ECO300',
    'ECO400',
    'ECO450',
    'ECO500',
    'ECO900',
    'ECOTUBO25',
    'ECOGRANITI35',
    'ECOGRANITI40',
    'ECOCHOPE',
    'ECOVIN',
    'ECOVIN25',
    'ECOCHAMP',
];
const SMALL_REFERENCES = [
    'ECO30',
    'ECO50',
    'ECO120',
    'ECO180',
    'ECO200',
    'ECO300',
    'ECOGRANITI35',
    'ECOGRANITI40',
    'ECOVIN',
    'ECOVIN25',
    'ECOCHAMP',
];
const SMALL_PLACED_REFERENCES = ['ECO400', 'ECO450', 'ECO500', 'ECOTUBO25'];
const LARGE_REFERENCES = ['ECO200', 'ECO300', 'ECO400', 'ECO450', 'ECO500', 'ECOTUBO25'];
const PLACEMENT_FILE_SUFFIX: Record<string, string> = { top: 'top', center: 'centre', bottom: 'bottom' };

const DELIVERED_FILES = [
    ...SCREEN_REFERENCES.map((code) => `${code}:screen`),
    ...SMALL_REFERENCES.map((code) => `${code}:small`),
    ...SMALL_PLACED_REFERENCES.flatMap((code) => ['top', 'center', 'bottom'].map((placement) => `${code}:small:${placement}`)),
    ...LARGE_REFERENCES.map((code) => `${code}:large`),
];

// Where the mock-up finds a delivered file, from its slot key.
function graduationFileUrl(slotKey: string): string {
    const [code, kind, placement] = slotKey.split(':') as [string, TGraduationSlotKind, string | undefined];
    const folder = { screen: 'mono', small: 'short', large: 'tall' }[kind];
    const suffix = placement ? `-${PLACEMENT_FILE_SUFFIX[placement]}` : '';
    return `/catalog/graduations/${folder}/${code}${suffix}.svg`;
}

// RES-02 lists them: classique, moderne, élégant, festif, universitaire, minimaliste,
// événementiel, saisonnier « et autres selon évolution ».
const ADMIN_GRAPHIC_STYLES: IAdminCategory[] = [
    { id: 'classique', label: 'Classique', translations: { en: 'Classic', pt: 'Clássico' } },
    { id: 'moderne', label: 'Moderne', translations: { en: 'Modern', pt: 'Moderno' } },
    { id: 'elegant', label: 'Élégant', translations: { en: 'Elegant', pt: 'Elegante' } },
    { id: 'festif', label: 'Festif', translations: { en: 'Festive', pt: 'Festivo' } },
    { id: 'universitaire', label: 'Universitaire', translations: { en: 'Campus', pt: 'Universitário' } },
    { id: 'minimaliste', label: 'Minimaliste', translations: { en: 'Minimalist', pt: 'Minimalista' } },
    { id: 'evenementiel', label: 'Événementiel', translations: { en: 'Events', pt: '' } },
    { id: 'saisonnier', label: 'Saisonnier', translations: { en: 'Seasonal', pt: 'Sazonal' } },
];

const GRAPHIC_STYLE_BY_THEME: Record<string, string> = {
    meteo: 'saisonnier',
    performance: 'moderne',
    humeur: 'festif',
    apero: 'festif',
    sport: 'moderne',
    parcours: 'minimaliste',
    decale: 'festif',
};

// The configurator's placeholder designs (gobeletcupGraduations), three per theme.
const PLACEHOLDER_STYLES: [string, string][] = [
    ['meteo', 'Thermomètre'],
    ['meteo', 'Flocons'],
    ['meteo', 'Soleil'],
    ['performance', 'Jauge'],
    ['performance', 'Pourcentage'],
    ['performance', 'Niveaux'],
    ['humeur', 'Sourires'],
    ['humeur', 'Étoiles'],
    ['humeur', 'Cœurs'],
    ['apero', 'Bulles'],
    ['apero', 'Tournées'],
    ['apero', 'Confettis'],
    ['sport', 'Chrono'],
    ['sport', 'Barres'],
    ['sport', 'Défis'],
    ['parcours', 'Étapes'],
    ['parcours', 'Jalons'],
    ['parcours', 'Flèches'],
    ['decale', 'Doses'],
    ['decale', 'Croix'],
    ['decale', 'Clins d’œil'],
];

const ADMIN_GRADUATION_STYLES: IAdminGraduationStyle[] = [
    {
        id: 'classiques-gobeletcup',
        name: localized('GOBELETCUP®', 'GOBELETCUP®', 'GOBELETCUP®'),
        themeId: 'classiques',
        graphicStyleId: 'classique',
        isActive: true,
        isRecolorable: true,
        compatiblePrintStyles: null,
        files: DELIVERED_FILES,
    },
    ...PLACEHOLDER_STYLES.map(([themeId, name]) => ({
        id: `${themeId}-${name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[^a-z]/g, '')}`,
        name: localized(name),
        themeId,
        graphicStyleId: GRAPHIC_STYLE_BY_THEME[themeId] ?? 'classique',
        isActive: false,
        isRecolorable: true,
        compatiblePrintStyles: null,
        files: [],
    })),
];

const ADMIN_RESOURCE_CATEGORIES: IAdminCategory[] = [
    { id: 'formes', label: 'Formes', translations: { en: 'Shapes', pt: 'Formas' } },
    { id: 'pictogrammes', label: 'Pictogrammes', translations: { en: 'Pictograms', pt: 'Pictogramas' } },
    { id: 'decoratifs', label: 'Décoratifs', translations: { en: 'Decorative', pt: '' } },
];

const ADMIN_PATTERN_CATEGORIES: IAdminCategory[] = [
    { id: 'geometriques', label: 'Géométriques', translations: { en: 'Geometric', pt: 'Geométricos' } },
    { id: 'festifs', label: 'Festifs', translations: { en: 'Festive', pt: 'Festivos' } },
];

function resource(categoryId: string, name: string, shape: IAdminLibraryAsset['shape']): IAdminLibraryAsset {
    const slug = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[^a-z]/g, '');
    return {
        id: `${categoryId}-${slug}`,
        kind: 'resource',
        name: localized(name),
        description: localized(''),
        categoryId,
        tags: localizedTags([slug]),
        svgFileName: `${slug}.svg`,
        shape,
        isRecolorable: true,
        sizeMm: 30,
        compatiblePrintStyles: null,
        referenceCodes: null,
        isActive: true,
    };
}

const MULTICOLOUR_STYLES = [EPrintStyle.QUADRI_SMALL_HEIGHT, EPrintStyle.QUADRI_LARGE_HEIGHT];

function pattern(categoryId: string, name: string, css: string, sizeMm: number): IAdminLibraryAsset {
    return {
        ...resource(categoryId, name, { pattern: css }),
        id: `motif-${categoryId}-${name.toLowerCase()}`,
        kind: 'pattern',
        sizeMm,
        // A one-colour motif follows the inks, so it is offered in every style (Katia, 2026-10-01).
        compatiblePrintStyles: null,
    };
}

const ADMIN_LIBRARY_ASSETS: IAdminLibraryAsset[] = [
    // The configurator's resource library, so both tools list the same pictograms.
    ...GOBELETCUP_GRAPHIC_RESOURCES.map((item) => resource(item.category, item.label, { icon: item.icon, path: item.path })),
    pattern('geometriques', 'Pois', 'radial-gradient(circle,currentColor_22%,transparent_24%)', 10),
    pattern('geometriques', 'Rayures', 'repeating-linear-gradient(90deg,currentColor_0_25%,transparent_25%_50%)', 12),
    pattern(
        'geometriques',
        'Vichy',
        'repeating-linear-gradient(0deg,rgb(0_0_0/0.35)_0_25%,transparent_25%_50%),repeating-linear-gradient(90deg,currentColor_0_25%,transparent_25%_50%)',
        16,
    ),
    pattern('geometriques', 'Chevrons', 'repeating-linear-gradient(45deg,currentColor_0_12%,transparent_12%_25%)', 14),
    pattern(
        'festifs',
        'Confettis',
        'radial-gradient(circle_at_20%_30%,currentColor_8%,transparent_9%),radial-gradient(circle_at_70%_70%,currentColor_6%,transparent_7%)',
        20,
    ),
    {
        ...pattern(
            'festifs',
            'Fiesta',
            'radial-gradient(circle_at_20%_30%,#E32636_8%,transparent_9%),radial-gradient(circle_at_70%_70%,#3478D4_6%,transparent_7%),radial-gradient(circle_at_75%_25%,#FFC400_6%,transparent_7%)',
            20,
        ),
        // Drawn in its own colours: nothing to recolour, so only in multicolour.
        isRecolorable: false,
        compatiblePrintStyles: MULTICOLOUR_STYLES,
    },
    {
        ...pattern('geometriques', 'Arlequin', 'conic-gradient(from_45deg,#E32636_0_25%,#FFC400_0_50%,#3478D4_0_75%,#36A342_0)', 16),
        isRecolorable: false,
        compatiblePrintStyles: MULTICOLOUR_STYLES,
    },
];

export {
    ADMIN_GRADUATION_STYLES,
    ADMIN_GRADUATION_THEMES,
    ADMIN_GRAPHIC_STYLES,
    ADMIN_LIBRARY_ASSETS,
    ADMIN_PATTERN_CATEGORIES,
    ADMIN_RESOURCE_CATEGORIES,
    graduationFileUrl,
};

export type { IAdminCategory, IAdminGraduationStyle, IAdminLibraryAsset, TGraduationSlotKind };
